import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { Match, AnimatedSwitch } from "@9amhealth/shared";
import CustomQuestionnaire from "components/CustomQuestionnaire/CustomQuestionnaire";
import CustomSuspense from "components/CustomSuspense/CustomSuspense";
import { CenterQuestionnaireContent } from "components/InAppQuestionnaire/InAppQuestionnaire";
import type { FC } from "react";
import { useEffect } from "react";
import { ONBOARDING_QUESTIONNAIRE_INITIAL_LAB } from "src/constants/misc";
import translate from "src/lib/translate";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import MedicalInsuranceForm from "src/ui/components/MedicalInsuranceForm/MedicalInsuranceForm";
import Track from "src/ui/components/Track/Track";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import { FirstStep } from "src/ui/components/AppQueryPopups/dialogs/RecurringLabQuestionnaireDialog/FirstStep";
import RecurringLabQuestionnaireCubit, {
  RecurringLabStep
} from "./RecurringLabQuestionnaireCubit";
import { Blac } from "blac-next";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";

const Container = styled.div`
  max-width: 600px;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  padding: var(--space-md);
  text-align: center;

  h1 {
    text-wrap: pretty;
  }
`;

const QuestionnaireWrapper = styled(CenterQuestionnaireContent)`
  nine-funnel-step {
    --funnel-step-padding: 4rem var(--space-md);
  }
`;

const RecurringLabQuestionnaireDialog: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();

  const [
    state,
    { updateInsuranceAvailability, nextStep, handleMedicalInsuranceSaved }
  ] = useBlocNext(RecurringLabQuestionnaireCubit);

  useEffect(() => {
    void updateInsuranceAvailability();
  }, []);

  const onClose = () => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  const refreshTasks = () => {
    void Blac.getBloc(TaskManagementBloc).loadProgramTasks(KnownProgram.CARE);
  };
  const [, { firstName }] = useBlocNext(UserPreferencesCubit);

  return (
    <InDialog
      width="min(calc(100% - 4rem), 120rem)"
      height="min(calc(100% - 4rem), 70rem)"
      padding="none"
      popup
      title={translate("task.consolidated_review_lab_status")}
      returnUrl={returnUrl}
      onClose={onClose}
    >
      <AnimatedSwitch value={state.activeStep}>
        <Match case={RecurringLabStep.Intro}>
          <FirstStep firstName={firstName ?? ""} onContinue={nextStep} />
        </Match>
        <Match case={RecurringLabStep.Questionnaire}>
          <QuestionnaireWrapper>
            <Track event="Initial Labs Dialog Questionnaire Shown" />
            <CustomSuspense>
              <CustomQuestionnaire
                resetScrollOnEachStep
                id={ONBOARDING_QUESTIONNAIRE_INITIAL_LAB}
                useRouting
                preventLoadingUserAnswers
                onLastStepCompleted={onClose}
                onDataSent={refreshTasks}
              />
            </CustomSuspense>
          </QuestionnaireWrapper>
        </Match>
        <Match case={RecurringLabStep.MedicalInsurance}>
          <Container>
            <Track event="Upload Medical Insurance Shown" />
            <MedicalInsuranceForm
              onStepChange={() => {}}
              onSaved={handleMedicalInsuranceSaved}
              closePopupOnSave={false}
            />
          </Container>
        </Match>
      </AnimatedSwitch>
    </InDialog>
  );
};

export default RecurringLabQuestionnaireDialog;
